import { Layout } from "antd";
import { Navigate, type RouteObject } from "react-router-dom";
import { DeleteAccountPage } from "@/modules/auth/pages/DeleteAccountPage";
import { LoginPage } from "@/modules/auth/pages/LoginPage";
import { RegisterPage } from "@/modules/auth/pages/RegisterPage";
import { BrigadeDetailPage } from "@/modules/brigades/pages/BrigadeDetailPage";
import { CreateAttendancePage } from "@/modules/daily-attendance/pages/CreateAttendancePage";
import { DailyAttendancePage } from "@/modules/daily-attendance/pages/DailyAttendancePage";
import { DashboardPage } from "@/modules/dashboard/pages/DashboardPage";
import { DevicesPage } from "@/modules/devices/pages/DevicesPage";
import { EquipmentDetailPage } from "@/modules/equipment/pages/EquipmentDetailPage";
import { EquipmentsPage } from "@/modules/equipment/pages/EquipmentsPage";
import { EquipmentTypesPage } from "@/modules/equipment-types/pages/EquipmentTypesPage";
import { FaceEnrollmentPage } from "@/modules/face-enrollment/pages/FaceEnrollmentPage";
import { MonitoringPage } from "@/modules/monitoring/pages/MonitoringPage";
import { NotificationsPage } from "@/modules/notifications/pages/NotificationsPage";
import { OrganizationTypePositionsPage } from "@/modules/organization-type-positions/pages/OrganizationTypePositionsPage";
import { OrganizationTypesPage } from "@/modules/organization-types/pages/OrganizationTypesPage";
import { OrganizationDetailPage } from "@/modules/organizations/pages/OrganizationDetailPage";
import { OrganizationsPage } from "@/modules/organizations/pages/OrganizationsPage";
import { OtherOrganizationsPage } from "@/modules/organizations/pages/OtherOrganizationsPage";
import { PlantContractsPage } from "@/modules/plant-contracts/pages/PlantContractsPage";
import { PlantingsPage } from "@/modules/plantings/pages/PlantingsPage";
import { PlantsPage } from "@/modules/plants/pages/PlantsPage";
import { StaffDetailPage } from "@/modules/staff/pages/StaffDetailPage";
import { StaffPage } from "@/modules/staff/pages/StaffPage";
import { StaffRouteHistoryPage } from "@/modules/staff-location/pages/StaffRouteHistoryPage";
import { StaffWorkdayPage } from "@/modules/staff-workday/pages/StaffWorkdayPage";
import { TaskSchedulePage } from "@/modules/task-schedule/pages/TaskSchedulePage";
import { TaskTypesPage } from "@/modules/task-types/pages/TaskTypesPage";
import { TaskDetailPage } from "@/modules/tasks/pages/TaskDetailPage";
import { TasksPage } from "@/modules/tasks/pages/TasksPage";
import { TerritoryDetailPage } from "@/modules/territories/pages/TerritoryDetailPage";
import { ProfilePage } from "@/modules/users/pages/ProfilePage";
import { UsersPage } from "@/modules/users/pages/UsersPage";
import { ComingSoonPage } from "@/shared/components/ComingSoonPage";
import { AuthLayout } from "@/shared/components/layouts/AuthLayout";
import { MainLayout } from "@/shared/components/layouts/MainLayout";
import { ProtectedRoute } from "./ProtectedRoute";
import { RouteErrorBoundary } from "./RouteErrorBoundary";

export const routes: RouteObject[] = [
	// Public routes - Auth
	{
		path: "/login",
		element: <AuthLayout />,
		errorElement: <RouteErrorBoundary />,
		children: [
			{
				index: true,
				element: <LoginPage />,
			},
			{
				path: "register",
				element: <RegisterPage />,
			},
		],
	},
	{
		path: "/delete-account",
		errorElement: <RouteErrorBoundary />,
		element: (
			<ProtectedRoute>
				<Layout className="min-h-screen bg-gray-100">
					<Layout.Content className="flex items-center justify-center p-6">
						<DeleteAccountPage />
					</Layout.Content>
				</Layout>
			</ProtectedRoute>
		),
	},

	// Protected routes
	{
		path: "/",
		element: (
			<ProtectedRoute>
				<MainLayout />
			</ProtectedRoute>
		),
		errorElement: <RouteErrorBoundary />,
		children: [
			{
				index: true,
				element: <Navigate to="/dashboard" replace />,
			},
			{
				path: "dashboard",
				element: <DashboardPage />,
			},
			{
				path: "tasks",
				element: <TasksPage />,
			},
			{
				path: "tasks/:id",
				element: <TaskDetailPage />,
			},
			{
				path: "task-types",
				element: <TaskTypesPage />,
			},
			{
				path: "task-schedule",
				element: <TaskSchedulePage />,
			},
			{
				path: "users",
				element: <UsersPage />,
			},
			{
				path: "organization-types",
				element: <OrganizationTypesPage />,
			},
			{
				path: "positions",
				element: <OrganizationTypePositionsPage />,
			},
			{
				path: "organizations",
				element: <OrganizationsPage />,
			},
			{
				path: "organizations/:id",
				element: <OrganizationDetailPage />,
			},
			{
				path: "brigades/:id",
				element: <BrigadeDetailPage />,
			},
			{
				path: "staff/:id",
				element: <StaffDetailPage />,
			},
			{
				path: "staff-location/:staffId/history",
				element: <StaffRouteHistoryPage />,
			},
			{
				path: "territories/:id",
				element: <TerritoryDetailPage />,
			},
			{
				path: "equipments",
				element: <EquipmentsPage />,
			},
			{
				path: "monitoring",
				element: <MonitoringPage />,
			},
			{
				path: "notifications",
				element: <NotificationsPage />,
			},
			{
				path: "equipment/:id",
				element: <EquipmentDetailPage />,
			},
			{
				path: "cross-access",
				element: <OtherOrganizationsPage />,
			},
			{
				path: "devices",
				element: <DevicesPage />,
			},
			// Plants (Ekinlar) routes
			{
				path: "plants",
				element: <PlantsPage />,
			},
			{
				path: "plant-contracts",
				element: <PlantContractsPage />,
			},
			{
				path: "plantings",
				element: <PlantingsPage />,
			},
			// Attendance routes
			{
				path: "attendance",
				element: <Navigate to="/attendance/daily" replace />,
			},
			{
				path: "attendance/daily",
				element: <DailyAttendancePage />,
			},
			{
				path: "attendance/employees",
				element: <CreateAttendancePage />,
			},
			{
				path: "attendance/faceid-devices",
				element: <ComingSoonPage title="FaceID qurilmalari holati" />,
			},
			{
				path: "payments/enter",
				element: <ComingSoonPage title="To'lovlarni kiritish" />,
			},
			{
				path: "payments/plan",
				element: <ComingSoonPage title="To'lov rejasini kiritish" />,
			},
			{
				path: "debts/groups",
				element: <ComingSoonPage title="Qarzdorliklar (Guruhlar kesimida)" />,
			},
			{
				path: "cameras",
				element: <ComingSoonPage title="Kameralar" />,
			},
			{
				path: "settings/cameras",
				element: <ComingSoonPage title="Kameralar sozlamalari" />,
			},
			{
				path: "settings/faceid",
				element: <DevicesPage />,
			},
			{
				path: "settings/staff",
				element: <StaffPage />,
			},
			{
				path: "settings/task-types",
				element: <TaskTypesPage />,
			},
			{
				path: "settings/equipment-types",
				element: <EquipmentTypesPage />,
			},
			{
				path: "settings/staff-workday",
				element: <StaffWorkdayPage />,
			},
			{
				path: "settings/profile",
				element: <ProfilePage />,
			},
			{
				path: "profile",
				element: <Navigate to="/settings/profile" replace />,
			},
			{
				path: "face-enrollment",
				element: <FaceEnrollmentPage />,
			},
		],
	},

	// 404 - Redirect to dashboard or login
	{
		path: "*",
		element: <Navigate to="/" replace />,
	},
];
